import AttachFileIcon from "@mui/icons-material/AttachFile";
import InputAdornment from "@mui/material/InputAdornment";
import { MuiFileInput } from "mui-file-input";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from "react-hook-form";

import { isAndroid } from "../../helpers/browser";
import {
  fileUploadTypePattern,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_ERROR,
} from "../../helpers/files";
import { setControllerFieldRef } from "../../helpers/form";
import { getNestedProperty } from "../../helpers/object";
import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";

interface APFileUploadProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  helperText?: string;
  showRequired?: boolean;
  highlightOnEmpty?: boolean;
  readOnly?: boolean;
  validations?: ValidationOptions;
  hidden?: boolean;
}

const APFileUpload = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  helperText,
  showRequired,
  highlightOnEmpty,
  readOnly,
  validations,
  hidden,
}: APFileUploadProps<T>) => {
  const error = getNestedProperty(name, errors);

  const processUploadedFile = async (file: File) => {
    // Detect if the user is on Android
    const _isAndroid = isAndroid();

    if (_isAndroid) {
      // Safely pull the virtual stream into RAM *only* for Android devices
      const arrayBuffer = await file.arrayBuffer();
      const safeBlob = new Blob([arrayBuffer], { type: file.type });
      const finalizedFile = new File([safeBlob], file.name, {
        type: file.type,
      });

      return finalizedFile;
    }

    // Desktop, iOS, and others stream directly from disk with zero RAM footprint
    return file;
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={{
        ...(validations
          ? validationOptionsToControlRules(label, validations)
          : {}),
      }}
      render={({ field: { ref, ...field } }) => (
        <MuiFileInput
          {...field}
          inputRef={(el) => setControllerFieldRef(ref, el)}
          label={showRequired ? `${label} *` : label}
          value={field.value}
          error={!!error || (highlightOnEmpty && !field.value)}
          onChange={async (file) => {
            if (file) {
              if (!fileUploadTypePattern.test(file.type)) {
                alert("Invalid file type");
              } else if (file.size > MAX_FILE_SIZE) {
                alert(`File ${MAX_FILE_SIZE_ERROR}.`);
              } else {
                const processedFile = await processUploadedFile(file);
                field.onChange(processedFile);
              }
            }
          }}
          disabled={readOnly}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ position: "absolute", right: "14px" }}
              >
                <AttachFileIcon />
              </InputAdornment>
            ),
            readOnly: readOnly,
          }}
          helperText={`${helperText || ""}`}
          required={validations?.required ?? false}
          sx={{
            display: hidden ? "none" : undefined,
          }}
        />
      )}
    />
  );
};

export default APFileUpload;
